import React from 'react'
import { Box } from '@mui/material'
import {
  ComposedChart,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Scatter,
  Line,
  Dot,
  DotProps,
  Area,
  CartesianGrid,
} from 'recharts'
import dayjs from 'dayjs'
import { tooltipStyles, chartWrapper, tooltipLabelStyles } from '../styles'

export type ChartData = {
  point: number
  threshold: number
  date: string
  current?: boolean
}

const chartStyles = {
  pointColor: '#BCBCBC',
  activePointColor: '#36C24C',
  invalidPointColor: '#e74c3c',
  lineColor: '#000000',
  areaColor: '#F5F5F5',
  gridColor: '#8d8d8d',
  tooltipBgColor: '#fff',
}

export type CustomDistanceChartProps = {
  data?: ChartData[]
}

const RenderDot = ({ cx, cy, payload }: DotProps & { payload?: ChartData }) => {
  const isCurrent = payload?.current ?? false
  const isAboveThreshold = payload?.point > (payload?.threshold || 0)

  return (
    <Dot
      cx={cx}
      cy={cy}
      fill={
        isCurrent
          ? isAboveThreshold
            ? chartStyles.invalidPointColor
            : chartStyles.activePointColor
          : chartStyles.pointColor
      }
      r={4}
    />
  )
}

export const CustomDistanceChart = ({ data }: CustomDistanceChartProps) => {
  if (!data || data.length === 0) return null

  const sortedData = [...data].sort(
    (a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf()
  )

  const maxThreshold = Math.max(...sortedData.map((d) => d.threshold))
  const maxPoint = Math.max(...sortedData.map((d) => d.point || 0))

  const paddedData = [
    {
      threshold: sortedData[0]?.threshold,
      point: null,
      date: dayjs(sortedData[0]?.date).subtract(1, 'd').toISOString(),
    },
    ...sortedData,
    {
      threshold: sortedData[sortedData?.length - 1]?.threshold,
      point: null,
      date: dayjs(sortedData[sortedData?.length - 1]?.date)
        .add(1, 'd')
        .toISOString(),
    },
  ]

  const processedData = []
  const uniqueDays = new Set()
  const dayIndices = {}

  sortedData.forEach((item) => {
    const date = dayjs(item.date)
    const dayStr = date.format('DD.MM')

    if (!uniqueDays.has(dayStr)) {
      uniqueDays.add(dayStr)
      dayIndices[dayStr] = []
    }
  })

  paddedData.forEach((item, index) => {
    const date = dayjs(item.date)
    const dayStr = date.format('DD.MM')
    const dayKey = date.format('YYYY-MM-DD')

    const processed = {
      ...item,
      originalDate: item.date,
      date: date.toISOString(),
      dayGroup: dayStr,
      dayKey: dayKey,
      time:
        item.point !== null && item.point !== undefined
          ? date.format('HH:mm')
          : '',
      index,
    }

    if (item.point !== null && item.point !== undefined) {
      dayIndices[dayStr].push(index)
    }
    processedData.push(processed)
  })

  const hasActualDays = uniqueDays.size >= 1

  const renderDayTick = (props) => {
    const { x, y, payload } = props

    const dayStr = payload.value
    const indices = dayIndices[dayStr] || []

    if (indices.length > 0) {
      const firstIndex = indices[0]

      if (payload.index === firstIndex) {
        const pathX = Math.floor(x) + 0.5
        return (
          <>
            <path
              d={`M${pathX},${y - 4}v${-25}`}
              stroke={chartStyles.invalidPointColor}
            />
            <text
              x={x + 5}
              y={y + 6}
              textAnchor="left"
              fill="#666"
              fontSize={12}
              transform={`rotate(45, ${x + 5}, ${y + 6})`}
            >
              {dayStr}
            </text>
          </>
        )
      }
    }

    return null
  }

  return (
    <Box sx={chartWrapper}>
      <ResponsiveContainer width="100%" height={250}>
        <ComposedChart
          data={processedData}
          margin={{ top: 10, right: 60, left: 60, bottom: 40 }}
        >
          <CartesianGrid stroke={chartStyles.gridColor} vertical={false} />
          <XAxis
            dataKey="time"
            axisLine
            tickLine
            interval={0}
            height={20}
            stroke={chartStyles.lineColor}
            angle={data.length > 10 ? 45 : 0}
            style={{
              fontSize: 12,
            }}
          />
          {hasActualDays && (
            <XAxis
              dataKey="dayGroup"
              xAxisId="date"
              axisLine={false}
              tickLine={false}
              height={30}
              tick={renderDayTick}
              interval={0}
            />
          )}
          <YAxis
            domain={[0, Math.floor(2 * Math.max(maxThreshold, maxPoint))]}
            tick={false}
            width={1}
          />
          <Tooltip
            itemSorter={(item) => {
              switch (item.dataKey) {
                case 'point':
                  return 1
                case 'threshold':
                  return 2
                default:
                  return 3
              }
            }}
            contentStyle={tooltipStyles}
            labelFormatter={(_value, items) => {
              if (!items || !items[0] || !items[0].payload)
                return 'Date: Unknown'

              try {
                return `Date: ${dayjs(items[0].payload.originalDate).format('DD.MM.YYYY HH:mm')}`
              } catch (e) {
                return 'Date: Invalid'
              }
            }}
            labelStyle={tooltipLabelStyles}
            itemStyle={tooltipLabelStyles}
            formatter={(value, name) => {
              if (name === 'hidden') return [null, null]
              return [
                typeof value === 'number' ? value.toFixed(2) : value,
                String(name).charAt(0).toUpperCase() + String(name).slice(1),
              ]
            }}
          />
          <Line
            type="monotone"
            dataKey="threshold"
            stroke={chartStyles.lineColor}
            strokeWidth={2}
            dot={false}
            activeDot={false}
            strokeDasharray="4 4"
          />
          <Area
            type="monotone"
            dataKey="threshold"
            stroke="none"
            name="hidden"
            dot={false}
            activeDot={false}
            fill={chartStyles.areaColor}
            fillOpacity={0.9}
          />
          <Scatter dataKey="point" shape={RenderDot} />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  )
}
